import React, { useState, useEffect, useMemo } from "react";
import { useGetList } from "ra-core";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GitBranch, Plus, Trash2, Split } from "lucide-react";
import type {
  ConditionConfig,
  ConditionBranch,
  ConditionRule,
  ConditionOperator,
  TriggerItem,
} from "./types";
import {
  ITEM_AVAILABLE_FIELDS,
  getOperatorsForFieldType,
  type ConditionFieldDef,
  type ActionFieldOption,
} from "./constants";
import { usePipelines } from "../deals/usePipelines";
import { useDealCustomFields } from "../deals/useDealCustomFields";
import type { Sale } from "../types";

interface ConditionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerItem: TriggerItem;
  config: ConditionConfig;
  onSave: (config: ConditionConfig) => void;
}

export const ConditionSheet: React.FC<ConditionSheetProps> = ({
  open,
  onOpenChange,
  triggerItem,
  config: initialConfig,
  onSave,
}) => {
  const { pipelines } = usePipelines();
  const { fields: dealCustomFields } = useDealCustomFields();
  const { data: salesList = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const [branches, setBranches] = useState<ConditionBranch[]>([]);

  // Funis reais cadastrados no CRM
  const pipelineOptions: ActionFieldOption[] = useMemo(() => {
    if (!pipelines || pipelines.length === 0) return [];
    return pipelines.map((pipe) => ({
      value: String(pipe.id),
      label: pipe.nome,
    }));
  }, [pipelines]);

  // Etapas reais do funil cadastradas no CRM
  const stageOptions: ActionFieldOption[] = useMemo(() => {
    if (!pipelines || pipelines.length === 0) return [];
    const seen = new Set<string>();
    const list: ActionFieldOption[] = [];
    for (const pipe of pipelines) {
      for (const st of pipe.etapas || []) {
        if (!seen.has(st.value)) {
          seen.add(st.value);
          list.push({
            value: st.value,
            label:
              pipelines.length > 1 ? `${st.label} (${pipe.nome})` : st.label,
          });
        }
      }
    }
    return list;
  }, [pipelines]);

  // Lista de vendedores / responsáveis do CRM
  const salesOptions: ActionFieldOption[] = useMemo(
    () =>
      salesList.map((s) => ({
        value: String(s.id),
        label: `${s.first_name} ${s.last_name || ""}`.trim(),
      })),
    [salesList],
  );

  // Campos disponíveis para filtragem (incluindo campos personalizados de negócio)
  const availableFields: ConditionFieldDef[] = useMemo(() => {
    const baseFields = ITEM_AVAILABLE_FIELDS[triggerItem] || [];
    if (triggerItem === "negocio" && dealCustomFields?.length > 0) {
      const customDefs: ConditionFieldDef[] = dealCustomFields.map((cf) => {
        const key = cf.id.startsWith("cf_") ? cf.id : `cf_${cf.id}`;
        const isSelect =
          (cf.type === "select" || cf.type === "autocomplete") &&
          cf.options &&
          cf.options.length > 0;
        const isUser = cf.type === "user";
        const isNumber = cf.type === "number" || cf.type === "currency";
        const isDate = cf.type === "date";

        return {
          field: key,
          label: `${cf.name} (Personalizado)`,
          type:
            isSelect || isUser
              ? "status"
              : isNumber
                ? "numero"
                : isDate
                  ? "data"
                  : "texto",
          inputType: isSelect
            ? "select"
            : isUser
              ? "sales_select"
              : isNumber
                ? "number"
                : isDate
                  ? "date"
                  : "text",
          options: cf.options?.map((opt) => ({ value: opt, label: opt })),
          placeholder: `Valor para ${cf.name}...`,
        };
      });

      const existing = new Set(baseFields.map((f) => f.field));
      return [
        ...baseFields,
        ...customDefs.filter((cf) => !existing.has(cf.field)),
      ];
    }
    return baseFields;
  }, [triggerItem, dealCustomFields]);

  // Obtém as opções de um campo de seleção (select, pipeline_select, stage_select, sales_select)
  const getFieldOptions = (
    fieldDef?: ConditionFieldDef,
  ): ActionFieldOption[] => {
    if (!fieldDef) return [];
    if (fieldDef.inputType === "pipeline_select") return pipelineOptions;
    if (fieldDef.inputType === "stage_select") return stageOptions;
    if (fieldDef.inputType === "sales_select") return salesOptions;
    return fieldDef.options || [];
  };

  // Cria uma regra inicial padronizada de acordo com o primeiro campo disponível
  const createDefaultRule = (fieldDef?: ConditionFieldDef): ConditionRule => {
    const target = fieldDef || availableFields[0];
    if (!target) {
      return { campo: "status", operador: "igual_a", valor: "aberto" };
    }
    const validOps = getOperatorsForFieldType(target.type);
    const opts = getFieldOptions(target);
    return {
      campo: target.field,
      operador: validOps[0]?.value || "igual_a",
      valor: opts.length > 0 ? opts[0].value : "",
    };
  };

  useEffect(() => {
    if (!open) return;
    if (initialConfig?.ramos && initialConfig.ramos.length > 0) {
      setBranches(JSON.parse(JSON.stringify(initialConfig.ramos)));
    } else {
      setBranches([
        {
          id: "r1",
          nome: "Se atendido",
          operador_logico: "e",
          regras: [createDefaultRule()],
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, triggerItem, open]);

  const handleAddRule = (branchIndex: number) => {
    const updated = [...branches];
    updated[branchIndex].regras.push(createDefaultRule());
    setBranches(updated);
  };

  const handleRemoveRule = (branchIndex: number, ruleIndex: number) => {
    const updated = [...branches];
    updated[branchIndex].regras.splice(ruleIndex, 1);
    if (updated[branchIndex].regras.length === 0) {
      updated[branchIndex].regras.push(createDefaultRule());
    }
    setBranches(updated);
  };

  const handleFieldSelectChange = (
    branchIndex: number,
    ruleIndex: number,
    newFieldKey: string,
  ) => {
    const updated = [...branches];
    const currentRule = updated[branchIndex].regras[ruleIndex];
    const fieldDef = availableFields.find((f) => f.field === newFieldKey);
    const validOps = getOperatorsForFieldType(fieldDef?.type || "texto");
    const isCurrentOpValid = validOps.some(
      (op) => op.value === currentRule.operador,
    );
    const nextOp = isCurrentOpValid
      ? currentRule.operador
      : validOps[0]?.value || "igual_a";

    const opts = getFieldOptions(fieldDef);
    const nextVal = opts.length > 0 ? opts[0].value : "";

    updated[branchIndex].regras[ruleIndex] = {
      campo: newFieldKey,
      operador: nextOp,
      valor: nextVal,
    };
    setBranches(updated);
  };

  const handleUpdateRule = (
    branchIndex: number,
    ruleIndex: number,
    field: keyof ConditionRule,
    value: any,
  ) => {
    const updated = [...branches];
    updated[branchIndex].regras[ruleIndex] = {
      ...updated[branchIndex].regras[ruleIndex],
      [field]: value,
    };
    setBranches(updated);
  };

  const handleAddBranch = () => {
    const nextId = `r${branches.length + 1}`;
    setBranches([
      ...branches,
      {
        id: nextId,
        nome: `Ramo ${branches.length + 1}`,
        operador_logico: "e",
        regras: [createDefaultRule()],
      },
    ]);
  };

  const handleRemoveBranch = (branchIndex: number) => {
    if (branches.length <= 1) return;
    const updated = [...branches];
    updated.splice(branchIndex, 1);
    setBranches(updated);
  };

  const handleSave = () => {
    onSave({
      ramos: branches,
    });
    onOpenChange(false);
  };

  // Renderiza o input de Valor Esperado casando exatamente com o formato do campo
  const renderExpectedValueInput = (
    rule: ConditionRule,
    bIdx: number,
    rIdx: number,
    fieldDef?: ConditionFieldDef,
  ) => {
    const currentVal = String(rule.valor ?? "");
    const options = getFieldOptions(fieldDef);

    if (
      fieldDef &&
      (fieldDef.inputType === "select" ||
        fieldDef.inputType === "pipeline_select" ||
        fieldDef.inputType === "stage_select" ||
        fieldDef.inputType === "sales_select") &&
      options.length > 0
    ) {
      const hasCustomVal =
        currentVal !== "" && !options.some((o) => o.value === currentVal);
      return (
        <Select
          value={currentVal || options[0]?.value || ""}
          onValueChange={(val) => handleUpdateRule(bIdx, rIdx, "valor", val)}
        >
          <SelectTrigger className="w-full h-8 text-xs bg-background overflow-hidden">
            <SelectValue placeholder={`Selecione ${fieldDef.label}`} />
          </SelectTrigger>
          <SelectContent>
            {hasCustomVal && (
              <SelectItem value={currentVal} className="text-xs font-mono">
                {currentVal}
              </SelectItem>
            )}
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (fieldDef?.inputType === "date") {
      return (
        <Input
          type="date"
          value={currentVal}
          onChange={(e) =>
            handleUpdateRule(bIdx, rIdx, "valor", e.target.value)
          }
          className="h-8 text-xs bg-background"
        />
      );
    }

    if (fieldDef?.inputType === "number") {
      return (
        <Input
          type="number"
          step="any"
          placeholder={fieldDef.placeholder || "Ex: 5000"}
          value={currentVal}
          onChange={(e) =>
            handleUpdateRule(bIdx, rIdx, "valor", e.target.value)
          }
          className="h-8 text-xs bg-background"
        />
      );
    }

    return (
      <Input
        type="text"
        placeholder={fieldDef?.placeholder || "Valor esperado"}
        value={currentVal}
        onChange={(e) => handleUpdateRule(bIdx, rIdx, "valor", e.target.value)}
        className="h-8 text-xs bg-background"
      />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col h-full overflow-y-auto gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
            <Split className="w-4 h-4 text-emerald-500" />
            Condições & Ramificações
          </div>
          <SheetTitle className="text-xl font-bold">
            Definir Regras de Decisão
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            O fluxo avaliará estas condições e seguirá pelo ramo correspondente.
            Se nenhuma condição for atendida, seguirá pelo caminho padrão.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-6 py-5 space-y-6">
          {branches.map((branch, bIdx) => (
            <div
              key={branch.id || bIdx}
              className="p-4 rounded-xl border border-border/80 bg-card/60 shadow-sm space-y-4"
            >
              {/* HEADER DO RAMO */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs">
                    {bIdx + 1}
                  </span>
                  <span className="font-semibold text-sm text-foreground">
                    {branch.nome || `Ramo ${bIdx + 1}`}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* TOGGLE E / OU */}
                  <div className="flex items-center rounded-lg border border-border/80 p-0.5 bg-muted/40">
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...branches];
                        updated[bIdx].operador_logico = "e";
                        setBranches(updated);
                      }}
                      className={`px-2 py-0.5 text-xs font-semibold rounded-md transition-colors cursor-pointer ${
                        (branch.operador_logico || "e") === "e"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      E (Todos)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...branches];
                        updated[bIdx].operador_logico = "ou";
                        setBranches(updated);
                      }}
                      className={`px-2 py-0.5 text-xs font-semibold rounded-md transition-colors cursor-pointer ${
                        branch.operador_logico === "ou"
                          ? "bg-primary text-primary-foreground shadow-xs"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      OU (Qualquer)
                    </button>
                  </div>

                  {branches.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBranch(bIdx)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* LISTA DE REGRAS */}
              <div className="space-y-3">
                {branch.regras.map((rule, rIdx) => {
                  const noValueNeeded =
                    rule.operador === "esta_vazio" ||
                    rule.operador === "nao_esta_vazio";
                  const fieldDef = availableFields.find(
                    (f) => f.field === rule.campo,
                  );
                  const validOperators = getOperatorsForFieldType(
                    fieldDef?.type || "texto",
                  );

                  return (
                    <div
                      key={rIdx}
                      className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-2.5 rounded-lg border border-border/60 bg-muted/20"
                    >
                      {/* Campo */}
                      <div className="w-full sm:w-2/5">
                        <Select
                          value={rule.campo}
                          onValueChange={(val) =>
                            handleFieldSelectChange(bIdx, rIdx, val)
                          }
                        >
                          <SelectTrigger className="w-full h-8 text-xs bg-background overflow-hidden">
                            <SelectValue placeholder="Selecione o campo" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableFields.map((f) => (
                              <SelectItem
                                key={f.field}
                                value={f.field}
                                className="text-xs"
                              >
                                {f.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Operador (filtrado de acordo com o tipo do campo) */}
                      <div className="w-full sm:w-2/5">
                        <Select
                          value={rule.operador}
                          onValueChange={(val) =>
                            handleUpdateRule(
                              bIdx,
                              rIdx,
                              "operador",
                              val as ConditionOperator,
                            )
                          }
                        >
                          <SelectTrigger className="w-full h-8 text-xs bg-background overflow-hidden">
                            <SelectValue placeholder="Operador" />
                          </SelectTrigger>
                          <SelectContent>
                            {validOperators.map((op) => (
                              <SelectItem
                                key={op.value}
                                value={op.value}
                                className="text-xs"
                              >
                                {op.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Valor esperado (formatado de acordo com o tipo do campo) */}
                      {!noValueNeeded && (
                        <div className="w-full sm:w-2/5">
                          {renderExpectedValueInput(rule, bIdx, rIdx, fieldDef)}
                        </div>
                      )}

                      {/* Botão Remover Regra */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={branch.regras.length <= 1}
                        onClick={() => handleRemoveRule(bIdx, rIdx)}
                        className="h-8 w-8 p-0 shrink-0 text-muted-foreground hover:text-rose-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* BOTÃO ADICIONAR REGRA */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleAddRule(bIdx)}
                className="h-7 text-xs gap-1 border-dashed"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar outra condição no {branch.nome || `Ramo ${bIdx + 1}`}
              </Button>
            </div>
          ))}

          {/* BOTÃO ADICIONAR OUTRO RAMO (SENÃO SE...) */}
          <Button
            type="button"
            variant="outline"
            onClick={handleAddBranch}
            className="w-full gap-2 border-dashed py-5 text-xs text-muted-foreground hover:text-foreground"
          >
            <GitBranch className="w-4 h-4 text-primary" />
            Adicionar Outro Ramo de Condição (Senão Se...)
          </Button>

          {/* AVISO DO CAMINHO PADRÃO */}
          <div className="p-3.5 rounded-lg border border-border/60 bg-muted/30 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              Caminho Padrão:{" "}
            </span>
            Se nenhum dos ramos acima for atendido no momento do disparo, o
            fluxo continuará automaticamente pelo caminho{" "}
            <em>"Nenhuma condição atendida"</em>.
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border/60 flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="gap-1.5 shadow-sm">
            Salvar Condições
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
